import { FC, useCallback, useRef, useEffect, useMemo, memo, useState } from 'react';
import { World as WorldType } from '@/types/core/World';
import { ThreeEvent, useThree, Canvas, ThreeElements } from '@react-three/fiber';
import { useAppContext } from '@/context/AppContext';
import { EffectComposer } from '@react-three/postprocessing';
import React, { ReactElement } from 'react';
import { useLinking } from '@/hooks/useLinking';
import { OrbitControls, Grid } from '@react-three/drei';
import * as THREE from 'three';
import { Item } from '@/types/core/Item';
import { TransformControls } from './components/TransformControls/TransformControls';

// Fix for Three.js elements not being available as JSX elements
declare global {
  namespace React {
    namespace JSX {
      interface IntrinsicElements extends ThreeElements {}
    }
  }
}

// Types
interface WorldProps {
  transformMode: 'translate' | 'rotate' | 'scale';
  worldId?: string; // Optional worldId to override the active world
}

interface WorldItemProps {
  item: Item;
  onSelect: (id: string) => void;
}

// Memoized WorldItem component for better performance
const WorldItem = memo<WorldItemProps>(({ item, onSelect }) => {
  // Create ref if it doesn't exist
  const ref = item.threeRef || React.createRef<THREE.Object3D | null>();
  if (!item.threeRef) {
    item.threeRef = ref;
  }

  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    
    // Only set pointer capture if target exists and is an Element
    if (e.target && 'setPointerCapture' in e.target) {
      (e.target as unknown as Element).setPointerCapture(e.pointerId);
    }
    
    onSelect(item.id);
  }, [item.id, onSelect]);

  // Render the item with the ref
  const element = item.render(item.properties, false, ref);
  
  if (!element || !React.isValidElement(element)) {
    return null;
  }

  // Use cloneElement with proper typing
  return React.cloneElement(
    element as ReactElement<any>,
    {
      key: item.id,
      onPointerDown: handlePointerDown,
      userData: { id: item.id }
    }
  );
});

// Memoized Effects component
const Effects: FC<{ items: Item[] }> = memo(({ items }) => {
  if (items.length === 0) return null;
  
  return (
    <EffectComposer>
      {items.map(effect => {
        if (!effect.threeRef) {
          effect.threeRef = React.createRef<THREE.Object3D | null>();
        }
        const element = effect.render(effect.properties, false, effect.threeRef);
        return React.isValidElement(element) ? 
          React.cloneElement(element as ReactElement<any>, { key: effect.id }) : 
          null;
      })}
    </EffectComposer>
  );
});

// The inner component that has access to Three.js context
const WorldContent: FC<{
  world: WorldType;
  transformMode: 'translate' | 'rotate' | 'scale';
}> = memo(({ world, transformMode }) => {
  const { selectItem, selectedItem } = useAppContext();
  
  // Reference to orbit controls for disabling during transform operations
  const orbitControlsRef = useRef<any>(null);
  
  // Initialize the linking system
  const linking = useLinking();
  
  // Force evaluation of links when the world changes
  useEffect(() => {
    linking.evaluateLinks();
  }, [world, linking]);

  // Handle click outside of objects to deselect
  const handlePointerMissed = useCallback(() => {
    selectItem(null);
  }, [selectItem]);

  // Memoize items to prevent unnecessary re-renders
  const items = useMemo(() => Object.values(world.items), [world.items]);
  
  // Separate effects from other items
  const effects = useMemo(() => 
    items.filter(item => item.type === 'effect'),
    [items]
  );

  return (
    <>
      {/* Add grid for reference */}
      <Grid infiniteGrid position={[0, -0.01, 0]} />
      
      {/* Render world objects */}
      <group 
        onPointerMissed={handlePointerMissed}
        onClick={(e: ThreeEvent<MouseEvent>) => e.stopPropagation()}
      >
        {items.map(item => (
          item.type !== 'effect' && (
            <WorldItem 
              key={item.id} 
              item={item} 
              onSelect={selectItem} 
            />
          )
        ))}
      </group>
      
      {/* Camera controls */}
      <OrbitControls
        ref={orbitControlsRef}
        makeDefault
        target={[0, 0, 0]}
      />

      {/* Transform controls for manipulating selected objects */}
      <TransformControls
        selectedItem={selectedItem}
        transformMode={transformMode}
        orbitControlsRef={orbitControlsRef}
      />

      {/* Render post-processing effects */}
      <Effects items={effects} />
    </>
  );
});

// Main World component
const World: FC<WorldProps> = ({ transformMode, worldId }) => {
  const { activeWorld } = useAppContext();
  
  // Use provided worldId or fall back to activeWorld
  const world = worldId && activeWorld?.id !== worldId 
    ? null // TODO: Add logic to fetch specific world if needed
    : activeWorld;
  
  // If no world is available, render nothing
  if (!world) {
    return null;
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas
        dpr={[1, 2]}
        gl={{ 
          preserveDrawingBuffer: true,
          antialias: true,
          alpha: true
        }}
      >
        <WorldContent world={world} transformMode={transformMode} />
      </Canvas>
    </div>
  );
};

export default World; 